import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import Icon3D from './Icon3D';

const PHONE_LENGTH = 10;

/**
 * Indian numbers read as 5+5, the same grouping the app's login screen uses.
 * Display only — state stays bare digits, so nothing downstream has to strip
 * the space back out.
 */
const formatPhone = (digits) =>
    digits.length > 5 ? `${digits.slice(0, 5)} ${digits.slice(5)}` : digits;

const LoginScreen = ({ onBack, onComplete, onNavigate }) => {
    const { login, verifyOtp } = useAuth();

    const [step, setStep] = useState(1);
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [phoneFocused, setPhoneFocused] = useState(false);

    const handleSendOtp = async (e) => {
        e.preventDefault();
        if (phone.length < 10) { setError('Please enter a valid 10-digit phone number'); return; }
        setError(''); setLoading(true);
        try { await login('+91', phone); setStep(2); }
        catch (err) { setError(err.response?.data?.message || 'Failed to send OTP. Please try again.'); }
        finally { setLoading(false); }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const finalOtp = otp.join('');
        if (finalOtp.length < 6) { setError('Please enter the complete 6-digit OTP'); return; }
        setError(''); setLoading(true);
        try { const { isNewUser } = await verifyOtp('+91', phone, finalOtp); onComplete(isNewUser); }
        catch (err) { setError(err.response?.data?.message || 'Invalid or expired OTP. Try again.'); }
        finally { setLoading(false); }
    };

    const handleOtpChange = (val, idx) => {
        if (isNaN(val)) return;
        const newOtp = [...otp]; newOtp[idx] = val; setOtp(newOtp);
        if (val !== '' && idx < 5) { const next = document.getElementById(`otp-${idx + 1}`); if (next) next.focus(); }
    };

    const handleKeyDown = (e, idx) => {
        if (e.key === 'Backspace' && otp[idx] === '' && idx > 0) {
            const prev = document.getElementById(`otp-${idx - 1}`); if (prev) prev.focus();
        }
    };

    return (
        <>
            <style>{`
                @keyframes modalIn {
                    from { opacity: 0; transform: scale(0.95) translateY(16px); }
                    to   { opacity: 1; transform: scale(1)   translateY(0);    }
                }
                @keyframes slideRight {
                    from { opacity: 0; transform: translateX(-16px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes fadeUp {
                    from { opacity: 0; transform: translateY(12px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .login-otp-box:focus {
                    border-color: #2563EB !important;
                    box-shadow: 0 0 0 4px rgba(34,106,70,0.12) !important;
                }
                .login-phone-input:focus-within {
                    border-color: #2563EB !important;
                    box-shadow: 0 0 0 4px rgba(34,106,70,0.10) !important;
                }
                .login-submit:hover:not(:disabled) {
                    transform: translateY(-2px) !important;
                    box-shadow: 0 12px 32px rgba(34,106,70,0.45) !important;
                }
                .login-submit:disabled { opacity: 0.7; cursor: not-allowed; }
                @media (max-width: 768px) {
                    .login-left-panel { display: none !important; }
                    .login-card { border-radius: 20px !important; max-width: 100% !important; }
                    .login-right-panel { padding: 32px 24px !important; }
                    .login-otp-box { width: 42px !important; height: 50px !important; font-size: 22px !important; border-radius: 12px !important; }
                }
            `}</style>

            {/* Overlay */}
            <div style={{
                position: 'fixed', inset: 0,
                background: 'rgba(10, 20, 60, 0.55)',
                backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px', zIndex: 2000,
            }}>
                {/* Card */}
                <div className="login-card" style={{
                    width: '100%', maxWidth: '860px',
                    borderRadius: '32px',
                    overflow: 'hidden',
                    display: 'flex',
                    boxShadow: '0 32px 80px rgba(0,0,0,0.28)',
                    animation: 'modalIn 0.35s cubic-bezier(0.34,1.56,0.64,1)',
                    position: 'relative',
                }}>

                    {/* ===== LEFT — Blue Gradient Panel ===== */}
                    <div className="login-left-panel" style={{
                        flex: '0 0 44%',
                        background: 'linear-gradient(155deg, #1E40AF 0%, #2563EB 55%, #3B82F6 100%)',
                        padding: '48px 36px',
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                        position: 'relative', overflow: 'hidden',
                    }}>
                        {/* Decorative orbs */}
                        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: 'rgba(255,255,255,0.07)', borderRadius: '50%', pointerEvents: 'none' }} />
                        <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '160px', height: '160px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', pointerEvents: 'none' }} />

                        <div style={{ position: 'relative', zIndex: 1 }}>
                            {/* Logo */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px' }}>
                                <img src="/flatloop.png" alt="FlatLoop" style={{ height: '36px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                                <span style={{ fontSize: '20px', fontWeight: '900', color: 'white', letterSpacing: '-0.5px' }}>FlatLoop</span>
                            </div>

                            <h2 style={{ fontSize: '28px', fontWeight: '800', color: 'white', lineHeight: '1.2', marginBottom: '14px', letterSpacing: '-0.5px' }}>
                                {step === 1 ? 'Find your perfect flat — free.' : 'Almost there!'}
                            </h2>
                            <p style={{ color: '#D9E6DC', fontSize: '15px', lineHeight: '1.7', marginBottom: '36px' }}>
                                {step === 1
                                    ? 'Rooms, flats and PG across ten Indian cities — with zero brokerage and no commission on any deal.'
                                    : `We sent a 6-digit OTP to +91 ${phone}. Enter it to complete login.`
                                }
                            </p>

                            {/* Trust badges */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                {[
                                    { icon: 'verified', text: 'Every account is phone-verified' },
                                    { icon: 'lock', text: 'Your data is safe & private' },
                                    { icon: 'chat', text: 'Direct chat — zero brokerage' },
                                ].map(b => (
                                    <div key={b.text} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        {/* Near-solid white, not a 15% tint: the 3D artwork is drawn
                                            for light grounds and turns into an unreadable blob on
                                            this blue panel otherwise. */}
                                        <div style={{ width: '32px', height: '32px', background: 'rgba(255,255,255,0.92)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon3D name={b.icon} size={19} /></div>
                                        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', fontWeight: '600' }}>{b.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Bottom note */}
                        <div style={{ position: 'relative', zIndex: 1, marginTop: '40px' }}>
                            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '16px', padding: '16px', border: '1px solid rgba(255,255,255,0.18)' }}>
                                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', margin: 0, lineHeight: '1.8' }}>
                                    By signing in, you agree to our{' '}
                                    <button
                                        type="button"
                                        onClick={() => onNavigate && onNavigate('terms')}
                                        style={{ background: 'none', border: 'none', color: 'white', fontWeight: '700', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                                    >Terms of Service</button>
                                    {' '}and{' '}
                                    <button
                                        type="button"
                                        onClick={() => onNavigate && onNavigate('privacy')}
                                        style={{ background: 'none', border: 'none', color: 'white', fontWeight: '700', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                                    >Privacy Policy</button>.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ===== RIGHT — White Form Panel ===== */}
                    <div className="login-right-panel" style={{
                        flex: 1,
                        background: 'white',
                        padding: '48px 40px',
                        display: 'flex', flexDirection: 'column', justifyContent: 'center',
                        position: 'relative',
                    }}>
                        {/* Close button */}
                        <button
                            onClick={onBack}
                            style={{ position: 'absolute', top: '24px', right: '24px', width: '40px', height: '40px', background: '#EFF6FF', border: 'none', borderRadius: '12px', fontSize: '22px', cursor: 'pointer', color: '#64748B', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
                            onMouseOver={e => e.currentTarget.style.background = '#E2E8F0'}
                            onMouseOut={e => e.currentTarget.style.background = '#EFF6FF'}
                        >×</button>

                        {/* Step indicator */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px' }}>
                            {[1, 2].map(s => (
                                <div key={s} style={{ height: '4px', borderRadius: '2px', transition: 'all 0.3s', flex: s === step ? 2 : 1, background: s <= step ? '#2563EB' : '#DDE3DC' }} />
                            ))}
                            <span style={{ fontSize: '12px', color: '#9CA3AF', fontWeight: '700', marginLeft: '4px', whiteSpace: 'nowrap' }}>Step {step} of 2</span>
                        </div>

                        {step === 1 ? (
                            <div key="step1" style={{ animation: 'slideRight 0.3s ease' }}>
                                <h3 style={{ fontSize: '26px', fontWeight: '800', color: '#111827', margin: '0 0 8px', letterSpacing: '-0.5px' }}>Enter your number</h3>
                                <p style={{ color: '#6B7280', fontSize: '15px', margin: '0 0 32px', lineHeight: '1.6' }}>We'll send a one-time password to verify you.</p>

                                {/* Error */}
                                {error && (
                                    <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#DC2626', borderRadius: '14px', padding: '14px 16px', fontSize: '14px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ fontSize: '18px' }}>⚠️</span> {error}
                                    </div>
                                )}

                                <form onSubmit={handleSendOtp}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>Phone Number</label>
                                    <div
                                        className="login-phone-input"
                                        onClick={() => document.getElementById('login-phone')?.focus()}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '12px',
                                            padding: '8px 16px 8px 8px',
                                            border: `1.5px solid ${error ? 'var(--status-error)' : phoneFocused ? 'var(--brand-primary)' : 'var(--border-glass)'}`,
                                            borderRadius: '18px',
                                            background: 'white',
                                            boxShadow: phoneFocused ? '0 4px 12px rgba(66,133,244,0.18)' : 'none',
                                            transition: 'border-color 0.2s, box-shadow 0.2s',
                                            marginBottom: '28px',
                                            cursor: 'text',
                                        }}
                                    >
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                                            height: '48px', padding: '0 14px', borderRadius: '13px',
                                            background: 'var(--brand-soft)', color: 'var(--brand-primary-dark)',
                                            fontWeight: 700, fontSize: '16px', flexShrink: 0,
                                        }}>
                                            <span aria-hidden="true">🇮🇳</span> +91
                                        </span>
                                        <input
                                            id="login-phone"
                                            type="tel"
                                            inputMode="numeric"
                                            autoComplete="tel"
                                            maxLength={PHONE_LENGTH + 1}
                                            placeholder="00000 00000"
                                            aria-label="Phone number"
                                            value={formatPhone(phone)}
                                            onChange={e => {
                                                setPhone(e.target.value.replace(/\D/g, '').slice(0, PHONE_LENGTH));
                                                setError('');
                                            }}
                                            onFocus={() => setPhoneFocused(true)}
                                            onBlur={() => setPhoneFocused(false)}
                                            style={{ flex: 1, minWidth: 0, padding: '16px 0', border: 'none', outline: 'none', fontSize: '21px', fontWeight: '700', letterSpacing: '1.5px', color: 'var(--text-dark)', background: 'transparent', fontFamily: 'inherit' }}
                                            autoFocus
                                        />
                                        {phone.length === PHONE_LENGTH && (
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--status-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
                                                <circle cx="12" cy="12" r="10" /><polyline points="8 12.5 11 15.5 16 9.5" />
                                            </svg>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        className="login-submit"
                                        disabled={loading || phone.length < 10}
                                        style={{ width: '100%', padding: '18px', background: 'linear-gradient(135deg, #1E40AF, #2563EB)', color: 'white', border: 'none', borderRadius: '18px', fontSize: '17px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 8px 24px rgba(34,106,70,0.3)', transition: 'all 0.2s', letterSpacing: '0.2px' }}
                                    >
                                        {loading ? (
                                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                <svg style={{ animation: 'spin 0.8s linear infinite' }} width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.35)" strokeWidth="3" /><path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round" /></svg>
                                                Sending OTP…
                                            </span>
                                        ) : 'Get OTP →'}
                                    </button>
                                </form>
                            </div>
                        ) : (
                            <div key="step2" style={{ animation: 'slideRight 0.3s ease' }}>
                                <h3 style={{ fontSize: '26px', fontWeight: '800', color: '#111827', margin: '0 0 8px', letterSpacing: '-0.5px' }}>Verify OTP</h3>
                                <p style={{ color: '#6B7280', fontSize: '15px', margin: '0 0 32px', lineHeight: '1.6' }}>
                                    Sent to <span style={{ color: '#1E40AF', fontWeight: '800' }}>+91 {phone}</span>
                                </p>

                                {/* Error */}
                                {error && (
                                    <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#DC2626', borderRadius: '14px', padding: '14px 16px', fontSize: '14px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ fontSize: '18px' }}>⚠️</span> {error}
                                    </div>
                                )}

                                <form onSubmit={handleVerifyOtp}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>6-Digit OTP</label>

                                    {/* OTP boxes */}
                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '28px' }}>
                                        {otp.map((digit, idx) => (
                                            <input
                                                key={idx}
                                                id={`otp-${idx}`}
                                                className="login-otp-box"
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={1}
                                                value={digit}
                                                onChange={e => handleOtpChange(e.target.value, idx)}
                                                onKeyDown={e => handleKeyDown(e, idx)}
                                                autoComplete="off"
                                                style={{
                                                    width: '52px', height: '60px',
                                                    border: `2px solid ${digit ? '#2563EB' : '#DDE3DC'}`,
                                                    borderRadius: '16px',
                                                    textAlign: 'center',
                                                    fontSize: '26px', fontWeight: '800',
                                                    outline: 'none',
                                                    background: digit ? '#EFF6FF' : '#F9FAFB',
                                                    color: '#1E40AF',
                                                    transition: 'all 0.15s',
                                                    fontFamily: 'inherit',
                                                }}
                                            />
                                        ))}
                                    </div>

                                    <button
                                        type="submit"
                                        className="login-submit"
                                        disabled={loading || otp.join('').length < 6}
                                        style={{ width: '100%', padding: '18px', background: 'linear-gradient(135deg, #1E40AF, #2563EB)', color: 'white', border: 'none', borderRadius: '18px', fontSize: '17px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 8px 24px rgba(34,106,70,0.3)', transition: 'all 0.2s', marginBottom: '16px' }}
                                    >
                                        {loading ? (
                                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                <svg style={{ animation: 'spin 0.8s linear infinite' }} width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.35)" strokeWidth="3" /><path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round" /></svg>
                                                Verifying…
                                            </span>
                                        ) : 'Verify & Sign In →'}
                                    </button>

                                    <div style={{ textAlign: 'center' }}>
                                        <button
                                            type="button"
                                            onClick={() => { setStep(1); setOtp(['', '', '', '', '', '']); setError(''); }}
                                            style={{ background: 'transparent', border: 'none', color: '#2563EB', cursor: 'pointer', fontSize: '14px', fontWeight: '700', padding: '4px 8px', borderRadius: '8px', transition: 'background 0.2s', fontFamily: 'inherit' }}
                                            onMouseOver={e => e.currentTarget.style.background = '#EFF6FF'}
                                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                            ← Change Phone Number
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div >
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>
    );
};

export default LoginScreen;
